/**
 * Creates an image element from a URL
 */
const createImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues
    image.src = url;
  });

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 * It creates a cropped version of an image based on the provided crop parameters
 */
const getCroppedImg = async (
  imageSrc: string | null,
  pixelCrop: { width: number; height: number; x: number; y: number } | null
) => {
  if (!imageSrc || !pixelCrop) {
    return null;
  }

  try {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    // Set the size of the cropped image
    const maxSize = Math.max(pixelCrop.width, pixelCrop.height);
    const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

    // Set canvas dimensions to double largest dimension to allow for a safe area for the
    // image to rotate in without being clipped by canvas context
    canvas.width = safeArea;
    canvas.height = safeArea;

    // Translate canvas context to a central location on image to allow rotating around the center
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.translate(-safeArea / 2, -safeArea / 2);

    // Draw rotated image and store data
    ctx.drawImage(
      image,
      safeArea / 2 - image.width * 0.5,
      safeArea / 2 - image.height * 0.5
    );
    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    // Set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Paste generated rotate image with correct offsets for x,y crop values
    ctx.putImageData(
      data,
      Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
      Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
    );

    // Determine the image format from the source
    let outputFormat = 'image/jpeg';
    const quality = 0.95;

    // Preserve the original image format if possible
    if (imageSrc.startsWith('data:image/png')) {
      outputFormat = 'image/png';
    } else if (imageSrc.startsWith('data:image/gif')) {
      outputFormat = 'image/gif';
    } else if (imageSrc.startsWith('data:image/webp')) {
      outputFormat = 'image/webp';
    }

    // Resize if the image is too large (max 500x500)
    const MAX_SIZE = 500;
    if (canvas.width > MAX_SIZE || canvas.height > MAX_SIZE) {
      const resizeCanvas = document.createElement('canvas');
      const resizeCtx = resizeCanvas.getContext('2d');

      if (!resizeCtx) {
        return canvas.toDataURL(outputFormat, quality);
      }

      const ratio = Math.min(MAX_SIZE / canvas.width, MAX_SIZE / canvas.height);
      resizeCanvas.width = canvas.width * ratio;
      resizeCanvas.height = canvas.height * ratio;

      resizeCtx.drawImage(
        canvas,
        0,
        0,
        canvas.width,
        canvas.height,
        0,
        0,
        resizeCanvas.width,
        resizeCanvas.height
      );

      return resizeCanvas.toDataURL(outputFormat, quality);
    }

    // Return the cropped image as a data URL
    return canvas.toDataURL(outputFormat, quality);
  } catch (error) {
    console.error('Error cropping image:', error);
    return null;
  }
};

export default getCroppedImg;
